// Planetary Defenses & random attacks (design doc 16.70, Don's direction).
//
// Two halves of one idea -- danger stops being purely something the
// captain opts into, and safety becomes something they can *build*:
//
// 1. **Planetary Defenses** are a buildable Base lot (`baseStorage.ts`'s
//    `PLANETARY_DEFENSES`). Once built, anything hostile that comes within
//    range of that planet is acquired and destroyed a few seconds later,
//    automatically. The captain neither crews nor fires it. This delivers
//    Section 6's locked rule that a player base is only as safe as its
//    defenses -- and gives the captain a real safe harbour to sit in.
// 2. **Random attacks** mean a captain in a dangerous System can be
//    jumped at any time, regardless of what they're doing -- not only as
//    a consequence of scanning or mining (16.1's existing per-action
//    ambient check, which stays). Deliberately irregular: a flat "every N
//    minutes" would become something you can clock-watch, which is the
//    opposite of the intent.
//
// The Base itself is never a target -- there is no base-siege mechanic
// and none is planned; defenses protect the *space around* the planet.

import { findBaseAt, isLotBuilt, PLANETARY_DEFENSES, type BaseRecord } from "./baseStorage";

// Engagement radius, measured from the planet object itself. Deliberately
// large relative to a Location's own 22,500m radius (Section 0) -- the
// point is a comfortable safe harbour the captain can actually notice and
// use, not a pinpoint bubble they have to hug the planet to stay inside.
export const PLANETARY_DEFENSE_RANGE_M = 10000;

// "Destroys within a few seconds" (Don's direction) -- long enough that
// the kill reads as a deliberate, visible event with its own beam and
// explosion rather than hostiles silently blinking out on arrival.
export const PLANETARY_DEFENSE_LOCK_MS = 3000;

// Random-attack spacing. Wide and uniform on purpose (see the header):
// the captain should never be able to predict the next one.
export const RANDOM_ATTACK_MIN_MS = 5 * 60 * 1000;
export const RANDOM_ATTACK_MAX_MS = 45 * 60 * 1000;

export type Rng = () => number; // uniform [0, 1)

export function nextRandomAttackDelayMs(rng: Rng = Math.random): number {
  return RANDOM_ATTACK_MIN_MS + rng() * (RANDOM_ATTACK_MAX_MS - RANDOM_ATTACK_MIN_MS);
}

// Tier 0 is "the explicit absence of danger... Sanctuary only" (Section
// 6), so the Safe Zone never rolls a random attack no matter how long the
// captain sits there.
export function systemAllowsRandomAttack(dangerTier: number): boolean {
  return dangerTier > 0;
}

// True only if the captain owns a Base at this exact System+planet AND has
// actually built its Planetary Defenses lot -- a bare lease protects
// nothing (Section 6: protection requires having "actually built and
// crewed defense turret buildings").
export function planetIsDefended(
  bases: BaseRecord[],
  systemId: string,
  planetId: string
): boolean {
  const base = findBaseAt(bases, systemId, planetId);
  return !!base && isLotBuilt(base, PLANETARY_DEFENSES);
}
